// Cookie consent + consent-gated Google Analytics (UK PECR/GDPR).
export type Consent = "accepted" | "rejected";
export const CONSENT_KEY = "snapshot-cookie-consent-v1";
export const OPEN_SETTINGS_EVENT = "snapshot:open-cookie-settings";

export function getConsent(): Consent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "rejected" ? v : null;
  } catch {
    return null;
  }
}

declare global {
  interface Window { dataLayer: unknown[]; gtag?: (...args: unknown[]) => void }
}

const MEASUREMENT_ID = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined;
let loaded = false;

/** Loads GA only when consent is "accepted". Never call before consent. */
export function loadAnalytics() {
  if (loaded || !MEASUREMENT_ID || getConsent() !== "accepted") return;
  loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag("set", "developer_id.dZjgwMW", true);
  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });
}

export function trackPageView(path: string) {
  if (!loaded || !window.gtag) return;
  window.gtag("event", "page_view", { page_path: path, page_location: window.location.href, page_title: document.title });
}

function clearGaCookies() {
  const host = window.location.hostname;
  const domains = ["", host, "." + host, "." + host.split(".").slice(-2).join(".")];
  document.cookie.split(";").map((c) => c.split("=")[0].trim()).filter((n) => n === "_ga" || n.startsWith("_ga_") || n === "_gid")
    .forEach((n) => domains.forEach((d) => {
      document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
    }));
}

export function setConsent(c: Consent) {
  try { localStorage.setItem(CONSENT_KEY, c); } catch { /* ignore */ }
  if (c === "accepted") loadAnalytics();
  else {
    clearGaCookies();
    if (loaded) window.location.reload(); // fully unload GA
  }
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}
